'use client'

import { createContext, useContext, useState, useEffect, useRef, useCallback, type ReactNode } from 'react'
import { trackAddToCart, trackRemoveFromCart, getVisitorId } from '@/lib/timed-ui/analytics'
import type { DepositPlan } from '@/lib/deposits/plan'

// ── Types ──

export interface CartItem {
  id: number
  eventId: string
  eventTitle: string
  eventStart?: number
  ticketTypeId: string
  ticketTypeName: string
  price: number
  allInPrice?: number
  quantity: number
  waveTimeId?: string | null
  waveTime?: number | null
  selectedDate?: string | null
  seatId?: string | null
  seatData?: {
    sectionName: string
    sectionKey?: string
    sectionType?: string
    rowLabel: string
    seatNumber: string
    categoryName?: string
  } | null
  // Season pass fields (optional)
  seasonId?: string | null
  seasonTicketTypeId?: string | null
  seasonPassType?: 'full' | 'flex' | 'package' | null
  seasonName?: string | null
  seasonSelectedEventIds?: string[] | null
}

/**
 * A deposit the cart qualifies for, as /api/checkout/describe computed it:
 * the plan's figures in minor units plus the same in major units for display.
 * Shown only; create-payment-intent recomputes it before charging.
 */
export interface DepositOffer extends DepositPlan {
  currency: string
  depositAmount: number
  balanceAmount: number
  totalAmount: number
  timezone: string | null
}

interface CartTotals {
  subtotal: number
  fees: number
  tax: number
  discount: number
  total: number
  feeBreakdown?: Array<{ name: string; amount: number }>
  /** Present only when the event offers a deposit and this cart qualifies. */
  deposit?: DepositOffer | null
}

interface CartState {
  items: CartItem[]
  promoCode: Record<string, unknown> | null
  totals: CartTotals
}

interface CartContextValue {
  cart: CartState
  isOpen: boolean
  itemCount: number
  addItem: (item: Omit<CartItem, 'id'>, options?: { openDrawer?: boolean }) => void
  removeItem: (itemId: number) => void
  updateQuantity: (itemId: number, quantity: number) => void
  applyPromoCode: (promoCodeData: Record<string, unknown>) => void
  removePromoCode: () => void
  updateTotals: (totals: CartTotals) => void
  clearCart: () => void
  toggleCart: () => void
  openCart: () => void
  closeCart: () => void
  serverCartId: string | null
  orderDescription: Record<string, unknown> | null
  describingOrder: boolean
  /**
   * True only while cart.totals are the server's answer (POST
   * /api/checkout/describe) for the cart exactly as it is now. Anything else
   * is a local subtotal with no fees, or totals kept from a describe that has
   * since failed; checkout must not show them as the total or take payment
   * against them.
   */
  totalsVerified: boolean
  /** The latest describe failed (rate limit, server error, network); it is retried with backoff. */
  describeFailed: boolean
  /** Run the last describe again now (checkout's "Try again"). */
  retryDescribe: () => void
  promoData: Record<string, unknown> | null
  setPromoData: (data: Record<string, unknown> | null) => void
  timeRemaining: number | null
  createServerCart: (customerInfo: Record<string, unknown>) => Promise<string>
  syncCartToServer: (cartId: string) => Promise<unknown>
  describeCurrentOrder: (promoCodes?: string[], promoDataOverride?: Record<string, unknown> | null, addonSubtotal?: number, donationSubtotal?: number, shopifySubtotal?: number) => Promise<unknown>
  /**
   * The server moved this cart's expiry later (a seat hold extension). The
   * local countdown clears the whole cart when it reaches zero, so it must
   * follow: otherwise checkout read "Your cart is empty" at the ORIGINAL
   * expiry while the server still held the seats for this buyer, and those
   * seats sat held for nobody until the extension ran out (GCA sellout test,
   * 11 minutes). Never shortens the countdown.
   */
  syncExpiryFromServer: (expiresAt: string | Date) => void
  /** Prevent cart expiration from clearing items (use during checkout) */
  lockCart: () => void
  unlockCart: () => void
  /**
   * Reconcile each local seated cart item against the server cart for the
   * given sessionId. The server is the source of truth for ticketTypeId,
   * ticketTypeName, and price — if a stale client wrote the wrong values to
   * localStorage at seat-click time, this overrides them on /checkout load.
   * Also PRUNES seated items the server flagged stale (hold released/seat
   * taken/ticket hidden) or that vanished server-side, returning how many
   * were removed so the caller can surface a message.
   */
  reconcileFromServer: (sessionId: string) => Promise<{ removedCount: number }>
}

// ── Context ──

const CartContext = createContext<CartContextValue | null>(null)

export const useTimedCart = () => {
  const context = useContext(CartContext)
  if (!context) {
    throw new Error('useTimedCart must be used within TimedCartProvider')
  }
  return context
}

// ── Helpers ──

let _nextCartItemId = Date.now()
function nextCartItemId(): number {
  return ++_nextCartItemId
}

const EMPTY_TOTALS: CartTotals = { subtotal: 0, fees: 0, tax: 0, discount: 0, total: 0 }
const EMPTY_CART: CartState = { items: [], promoCode: null, totals: EMPTY_TOTALS }

const DEFAULT_TIMEOUT_MS = 15 * 60 * 1000 // 15 min (matches server-side InventoryHold expiration)

function recalculateLocalTotals(items: CartItem[]): CartTotals {
  // Use base price only — fees/tax are calculated by the backend
  const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0)
  return { subtotal, fees: 0, tax: 0, discount: 0, total: subtotal }
}

/**
 * What describe prices, as one string: a cart whose key changed needs a new
 * describe before its totals mean anything. Checkout re-describes on this same
 * key, so any change here is a change it reacts to.
 */
export function describeItemsKey(items: CartItem[]): string {
  return items.map(i => `${i.seatId || ''}:${i.ticketTypeId}:${i.quantity}:${i.price}`).join(',')
}

/**
 * Is this response a describe answer? Error responses are JSON too: the per-IP
 * limiter answers 429 {success:false, error}, a server error 500 {success:false,
 * error}. Read as totals, those parsed to fees 0 and total 0, so checkout showed
 * Freedom 43's $900.00 for a $972.00 charge, and a cart with a partial promo
 * code turned into a $0.00 "free" order the server then refused.
 */
export function isDescribeAnswer(ok: boolean, data: unknown): boolean {
  if (!ok || !data || typeof data !== 'object') return false
  const d = data as Record<string, unknown>
  if (d.success === false) return false
  const total = d.total ?? d.grandTotal
  return total != null && Number.isFinite(parseFloat(String(total)))
}

// Backoff for a failed describe. The 429 that trips it is a per-minute window,
// so the last waits clear a full minute; a Retry-After from the server wins
// when it is longer. After these, the buyer's "Try again" starts it over.
const DESCRIBE_RETRY_DELAYS_MS = [1_000, 2_000, 4_000, 8_000, 16_000, 30_000]

type DescribeArgs = [string[], Record<string, unknown> | null, number, number, number]

function loadCart(): CartState {
  // Always called from useEffect, so localStorage is available — but
  // keep the SSR guard as a safety net in case it's ever called sync.
  if (typeof window === 'undefined') return EMPTY_CART
  try {
    const saved = localStorage.getItem('ticketCart')
    if (saved) {
      const cart = JSON.parse(saved) as CartState
      // Ensure the ID counter is above any existing item IDs to prevent collisions
      for (const item of cart.items) {
        if (item.id >= _nextCartItemId) {
          _nextCartItemId = item.id + 1
        }
      }
      return cart
    }
  } catch { /* ignore */ }
  return EMPTY_CART
}

function loadExpiresAt(): number | null {
  if (typeof window === 'undefined') return null
  const saved = localStorage.getItem('cartExpiresAt')
  return saved ? parseInt(saved, 10) : null
}

// ── Provider ──

export const TimedCartProvider = ({ children }: { children: ReactNode }) => {
  // Initial state must match SSR (empty) so React doesn't see a
  // hydration mismatch. Cart loaded from localStorage in the
  // useEffect below, post-mount.
  const [cart, setCart] = useState<CartState>(EMPTY_CART)
  const [isOpen, setIsOpen] = useState(false)
  const [serverCartId, setServerCartId] = useState<string | null>(null)
  const [orderDescription, setOrderDescription] = useState<Record<string, unknown> | null>(null)
  const [describingOrder, setDescribingOrder] = useState(false)
  // The describeItemsKey of the cart the last SUCCESSFUL describe priced:
  // cart.totals are the server's only while it matches the cart as it is now.
  const [describedKey, setDescribedKey] = useState<string | null>(null)
  const [describeFailed, setDescribeFailed] = useState(false)
  // The newest describe call owns the result, so a slow older response can
  // never overwrite a newer cart's totals; it also cancels a pending retry.
  const describeSeqRef = useRef(0)
  const describeRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const describeRetryCountRef = useRef(0)
  const lastDescribeArgsRef = useRef<DescribeArgs | null>(null)
  const describeRef = useRef<((...args: DescribeArgs) => Promise<unknown>) | null>(null)
  const [promoData, setPromoData] = useState<Record<string, unknown> | null>(null)
  const [cartExpiresAt, setCartExpiresAt] = useState<number | null>(null)
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null)
  const [cartTimeoutMs, setCartTimeoutMs] = useState(DEFAULT_TIMEOUT_MS)
  const cartTimeoutMsRef = useRef(DEFAULT_TIMEOUT_MS)
  const clearCartRef = useRef<(() => void) | null>(null)
  const cartLockedRef = useRef(false)

  // Fetch configurable cart timeout from checkout settings
  // Only updates the ref/state for FUTURE addItem calls — never recalculates
  // an active cartExpiresAt, because it may have been set by a seat hold
  // with its own event-level expiration.
  useEffect(() => {
    fetch('/api/checkout/settings')
      .then(res => res.json())
      .then(data => {
        if (data.success && data.settings?.cartTimeoutMinutes) {
          const newTimeoutMs = data.settings.cartTimeoutMinutes * 60 * 1000
          cartTimeoutMsRef.current = newTimeoutMs
          setCartTimeoutMs(newTimeoutMs)
        }
      })
      .catch(() => {})
  }, [])

  // Hydrate from localStorage post-mount. Server rendered with empty
  // cart so this is the first time the real cart contents are applied.
  useEffect(() => {
    const cached = loadCart()
    if (cached.items.length > 0) setCart(cached)
    const expiresAt = loadExpiresAt()
    if (expiresAt) setCartExpiresAt(expiresAt)
  }, [])

  // Persist cart to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('ticketCart', JSON.stringify(cart))
    }
  }, [cart])

  // Persist expiration
  useEffect(() => {
    if (cartExpiresAt) {
      localStorage.setItem('cartExpiresAt', String(cartExpiresAt))
    } else {
      localStorage.removeItem('cartExpiresAt')
    }
  }, [cartExpiresAt])

  // Countdown timer - uses visibilitychange to recover from background tab throttling
  useEffect(() => {
    if (!cartExpiresAt || cart.items.length === 0) {
      setTimeRemaining(null)
      return
    }

    const tick = () => {
      const remaining = Math.max(0, cartExpiresAt - Date.now())
      setTimeRemaining(remaining)
      if (remaining <= 0 && clearCartRef.current && !cartLockedRef.current) {
        clearCartRef.current()
      }
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        tick()
      }
    }

    tick()
    const interval = setInterval(tick, 1000)
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [cartExpiresAt, cart.items.length])

  // Check if cart expired on mount
  useEffect(() => {
    if (cart.items.length > 0 && cartExpiresAt && cartExpiresAt <= Date.now() && !cartLockedRef.current) {
      setCart(EMPTY_CART)
      setCartExpiresAt(null)
      localStorage.removeItem('ticketCart')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const syncExpiryFromServer = useCallback((expiresAt: string | Date) => {
    const t = new Date(expiresAt).getTime()
    if (!Number.isFinite(t) || t <= Date.now()) return
    setCartExpiresAt(prev => (prev && prev > t ? prev : t))
  }, [])

  const clearCart = useCallback(() => {
    setCart(EMPTY_CART)
    setServerCartId(null)
    setOrderDescription(null)
    setPromoData(null)
    setCartExpiresAt(null)
    // Nothing left to describe: forget the last answer and any pending retry.
    describeSeqRef.current++
    if (describeRetryTimerRef.current) clearTimeout(describeRetryTimerRef.current)
    describeRetryTimerRef.current = null
    describeRetryCountRef.current = 0
    setDescribedKey(null)
    setDescribeFailed(false)
    setDescribingOrder(false)
    localStorage.removeItem('ticketCart')

    // Clean up all seat-related storage entries
    if (typeof window !== 'undefined') {
      localStorage.removeItem('seatCartId')
      // Clean localStorage seat hold keys
      const keysToRemove: string[] = []
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i)
        if (key && (key.startsWith('seat-session-') || key.startsWith('seat-hold-'))) {
          keysToRemove.push(key)
        }
      }
      keysToRemove.forEach(k => localStorage.removeItem(k))
      // Also clean legacy sessionStorage entries
      sessionStorage.removeItem('seatCartId')
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const key = sessionStorage.key(i)
        if (key && (key.startsWith('seat-session-') || key.startsWith('seat-hold-'))) {
          sessionStorage.removeItem(key)
        }
      }
    }
  }, [])

  clearCartRef.current = clearCart

  const addItem = useCallback((item: Omit<CartItem, 'id'>, { openDrawer = true } = {}) => {
    setCart(prev => {
      // For seated items (seatId present), each seat is a separate cart item.
      // For GA/timed items, merge by eventId + ticketTypeId + waveTimeId.
      const existingIdx = prev.items.findIndex(
        i => i.eventId === item.eventId &&
          i.ticketTypeId === item.ticketTypeId &&
          i.waveTimeId === item.waveTimeId &&
          i.seatId === item.seatId
      )

      let newItems: CartItem[]
      if (existingIdx >= 0) {
        newItems = [...prev.items]
        newItems[existingIdx] = {
          ...newItems[existingIdx],
          quantity: Math.min(newItems[existingIdx].quantity + item.quantity, 10),
        }
      } else {
        newItems = [...prev.items, { ...item, id: nextCartItemId() }]
      }

      return { ...prev, items: newItems, promoCode: null, totals: recalculateLocalTotals(newItems) }
    })
    setPromoData(null)
    // Set cart expiration based on item type:
    // - Seat items: use the server-managed hold expiration (event-level config)
    // - GA/timed items: use the global cart timeout from checkout settings
    // Use the LATEST expiry so seated items aren't killed by a shorter GA timeout
    const timeout = cartTimeoutMsRef.current
    if (item.seatId && typeof window !== 'undefined') {
      const holdExpiry = localStorage.getItem(`seat-hold-expires-${item.eventId}`)
      if (holdExpiry) {
        const newExpiry = new Date(holdExpiry).getTime()
        setCartExpiresAt(prev => {
          // Seat holds use the later expiry — never let a shorter GA timeout kill seat items
          if (!prev || prev <= Date.now()) return newExpiry
          return Math.max(prev, newExpiry)
        })
      } else {
        setCartExpiresAt(prev => prev && prev > Date.now() ? prev : Date.now() + timeout)
      }
    } else {
      // GA/timed items: set expiry, but never shorten an existing seat hold expiry
      setCartExpiresAt(prev => {
        const gaExpiry = Date.now() + timeout
        if (!prev || prev <= Date.now()) return gaExpiry
        return Math.max(prev, gaExpiry)
      })
    }
    if (openDrawer) setIsOpen(true)

    // Fire tracking event
    trackAddToCart(
      [{ ticketTypeId: item.ticketTypeId, ticketTypeName: item.ticketTypeName, quantity: item.quantity, price: item.price, eventName: item.eventTitle }],
      item.price * item.quantity,
    )
  }, []) // cartTimeoutMs read from ref to avoid stale closures
  const removeItem = useCallback((itemId: number) => {
    setCart(prev => {
      const removed = prev.items.find(item => item.id === itemId)
      if (removed) {
        trackRemoveFromCart(
          [{ ticketTypeId: removed.ticketTypeId, ticketTypeName: removed.ticketTypeName, quantity: removed.quantity, price: removed.price, eventName: removed.eventTitle }],
          removed.price * removed.quantity,
        )

        // Release holds on server when removing any item (seat hold + inventory hold)
        if (typeof window !== 'undefined') {
          const eventId = removed.eventId
          const seatId = removed.seatId
          const holdSessionId = localStorage.getItem(`seat-session-${eventId}`)
            || localStorage.getItem(`seat-hold-session-${eventId}`)
          if (holdSessionId) {
            fetch('/api/cart/items/remove-seat', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                eventId,
                seatId: seatId || undefined,
                sessionId: holdSessionId,
                ticketTypeId: removed.ticketTypeId || undefined,
              }),
            }).catch((err) => {
              console.warn('[Cart] Failed to release hold:', err)
            })
          }
        }
      }
      const newItems = prev.items.filter(item => item.id !== itemId)
      // If no more seat items remain, clean up seatCartId
      if (typeof window !== 'undefined' && !newItems.some(i => i.seatId)) {
        localStorage.removeItem('seatCartId')
      }
      return { ...prev, items: newItems, totals: recalculateLocalTotals(newItems) }
    })
  }, [])

  const updateQuantity = useCallback((itemId: number, quantity: number) => {
    if (quantity <= 0) {
      removeItem(itemId)
      return
    }
    const capped = Math.min(quantity, 10)
    setCart(prev => {
      const newItems = prev.items.map(item =>
        item.id === itemId ? { ...item, quantity: capped } : item
      )
      return { ...prev, items: newItems, totals: recalculateLocalTotals(newItems) }
    })
  }, [removeItem])

  const applyPromoCode = useCallback((promoCodeData: Record<string, unknown>) => {
    setCart(prev => ({ ...prev, promoCode: promoCodeData }))
  }, [])

  const removePromoCode = useCallback(() => {
    setCart(prev => ({ ...prev, promoCode: null }))
  }, [])

  const updateTotals = useCallback((totals: CartTotals) => {
    setCart(prev => ({ ...prev, totals }))
  }, [])

  const toggleCart = useCallback(() => setIsOpen(prev => !prev), [])
  const openCart = useCallback(() => setIsOpen(true), [])
  const closeCart = useCallback(() => setIsOpen(false), [])

  // ── Server Operations ──

  const createServerCart = useCallback(async (customerInfo: Record<string, unknown>): Promise<string> => {
    const res = await fetch('/api/cart', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(customerInfo),
    })
    const data = await res.json()
    const cartId = data.cartId || data.id || data.data?.id
    setServerCartId(cartId)
    return cartId
  }, [])

  const syncCartToServer = useCallback(async (cartId: string) => {
    const tickets = cart.items.map(item => ({
      eventId: String(item.eventId),
      quantity: item.quantity,
      typeId: String(item.ticketTypeId),
      waveTimeId: item.waveTimeId ? String(item.waveTimeId) : undefined,
    }))
    const res = await fetch('/api/cart/items', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ cartId, tickets }),
    })
    const data = await res.json()
    if (data.error || data.errors) {
      const msg = data.error || (Array.isArray(data.errors) ? data.errors.join(', ') : data.errors)
      throw new Error(msg || 'Some tickets are no longer available')
    }
    return data
  }, [cart.items])

  /**
   * Ask the server for this cart's totals (POST /api/checkout/describe).
   *
   * Only a describe ANSWER becomes cart.totals. When the request fails (the
   * per-IP limiter's 429, a 500, a dropped connection), the last good server
   * totals are kept but no longer count as verified for this cart, the call is
   * retried with backoff, and it resolves to null rather than throwing: the
   * buyer is told and can retry, and nothing upstream turns the failure into
   * a total without fees or into a free order.
   */
  const describeCurrentOrder = useCallback(async (promoCodes: string[] = [], promoDataOverride: Record<string, unknown> | null = null, addonSubtotal: number = 0, donationSubtotal: number = 0, shopifySubtotal: number = 0) => {
    const seq = ++describeSeqRef.current
    if (describeRetryTimerRef.current) clearTimeout(describeRetryTimerRef.current)
    describeRetryTimerRef.current = null
    lastDescribeArgsRef.current = [promoCodes, promoDataOverride, addonSubtotal, donationSubtotal, shopifySubtotal]
    const itemsKey = describeItemsKey(cart.items)
    let retryAfterMs = 0
    setDescribingOrder(true)
    try {
      // Use base price — backend calculates fees/tax authoritatively
      // addonSubtotal: optional extra from venue/global addons selected at checkout (not in cart.items)
      const ticketSubtotal = cart.items.reduce((sum, item) => sum + item.price * item.quantity, 0)
      const localSubtotal = ticketSubtotal + addonSubtotal

      const res = await fetch('/api/checkout/describe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          // Only so the rate limiter counts this shopper on their own rather
          // than everyone behind the same carrier IP (lib/buyer-rate-key).
          sessionId: getVisitorId() || undefined,
          items: cart.items.map(item => ({
            eventId: item.eventId,
            ticketTypeId: item.ticketTypeId,
            quantity: item.quantity,
            // Per-unit price is required for BOGO math (cheapest-ticket-free
            // needs unit prices, not just the subtotal) — omitting it zeroed
            // BOGO discounts at checkout while apply reported success.
            price: item.price,
            waveTimeId: item.waveTimeId || null,
            // A reserved seat or seat-map zone rules a deposit out (v1).
            ...(item.seatId || item.seatData ? { hasSeat: true } : {}),
          })),
          // Season passes are priced, and charged their season's own fee and
          // tax, by the server. Without these the total on screen was the
          // pass price alone while checkout charged the fee on top.
          seasonItems: cart.items
            .filter(item => item.seasonId)
            .map(item => ({
              seasonId: item.seasonId,
              seasonTicketTypeId: item.seasonTicketTypeId,
              quantity: item.quantity,
              seatId: item.seatId || null,
            })),
          promoCodes,
          subtotal: ticketSubtotal,          // tickets only (promo applies here)
          addonSubtotal: addonSubtotal,      // addons separate (promo does NOT apply)
          donationSubtotal: donationSubtotal, // donations separate (may be exempt from fees/tax)
          shopifySubtotal: shopifySubtotal,  // merch subset of addons (fee-exempt when tenant toggled)
        }),
      })

      const data = await res.json().catch(() => null)
      if (!isDescribeAnswer(res.ok, data)) {
        retryAfterMs = (parseInt(res.headers?.get('Retry-After') || '', 10) || 0) * 1000
        throw new Error(`Order totals unavailable (HTTP ${res.status})`)
      }
      // A newer describe was asked for while this one was in flight: its
      // answer is the one that counts.
      if (seq !== describeSeqRef.current) return data
      setOrderDescription(data)

      const apiSubtotal = parseFloat(data.subtotal ?? 0) || localSubtotal
      const apiFees = parseFloat(data.fees ?? data.serviceFee ?? 0)
      const apiTax = parseFloat(data.tax ?? 0)
      const apiDiscount = parseFloat(data.discount ?? 0)
      const apiTotal = parseFloat(data.total ?? data.grandTotal ?? 0)

      const apiFeeBreakdown = Array.isArray(data.feeBreakdown) ? data.feeBreakdown : undefined

      // Server value is authoritative — it's what process-payment charges.
      // Preferring the LOCAL subtotal here let a stale localStorage price
      // show a total that differed from the amount actually charged. Fall
      // back to local only if the server returned nothing usable.
      const totals: CartTotals = {
        subtotal: apiSubtotal > 0 ? apiSubtotal : localSubtotal,
        fees: apiFees,
        tax: apiTax,
        discount: apiDiscount,
        total: apiTotal,
        feeBreakdown: apiFeeBreakdown,
        ...(data?.deposit?.eligible ? { deposit: data.deposit as DepositOffer } : {}),
      }

      // Legacy fallback: if the API returned total=0 with no discount value
      // (older describe responses didn't always populate `discount`), infer
      // the discount from the components. Only kicks in when total really
      // is 0 — when addons bring total above zero, the API value is right
      // and we don't override.
      if (
        promoCodes.length > 0 &&
        totals.subtotal > 0 &&
        apiTotal === 0 &&
        totals.discount < totals.subtotal + totals.fees + totals.tax - 0.01
      ) {
        totals.discount = totals.subtotal + totals.fees + totals.tax
      }

      const componentSum = Math.max(0, totals.subtotal + totals.fees + totals.tax - totals.discount)
      if (Math.abs(totals.total - componentSum) > 0.01) {
        totals.total = componentSum
      }

      updateTotals(totals)
      setDescribedKey(itemsKey)
      setDescribeFailed(false)
      describeRetryCountRef.current = 0
      return data
    } catch (err) {
      if (seq !== describeSeqRef.current) return null
      // Keep the last good server totals; do NOT stand in a local subtotal
      // with no fees, which is what checkout used to show and quote. The
      // totals stop counting as verified until a describe succeeds again.
      console.error('[describe] Order totals unavailable:', err)
      setDescribeFailed(true)
      const attempt = describeRetryCountRef.current++
      if (attempt < DESCRIBE_RETRY_DELAYS_MS.length) {
        describeRetryTimerRef.current = setTimeout(() => {
          describeRetryTimerRef.current = null
          const args = lastDescribeArgsRef.current
          if (args) void describeRef.current?.(...args)
        }, Math.max(DESCRIBE_RETRY_DELAYS_MS[attempt], retryAfterMs))
      }
      return null
    } finally {
      if (seq === describeSeqRef.current) setDescribingOrder(false)
    }
  }, [cart.items, promoData, updateTotals])
  // The retry timer calls the newest describe, which sees the newest cart.
  describeRef.current = describeCurrentOrder

  const retryDescribe = useCallback(() => {
    const args = lastDescribeArgsRef.current
    if (!args) return
    describeRetryCountRef.current = 0
    void describeRef.current?.(...args)
  }, [])

  // A retry must not fire into an unmounted provider.
  useEffect(() => () => {
    if (describeRetryTimerRef.current) clearTimeout(describeRetryTimerRef.current)
  }, [])

  const totalsVerified = !describeFailed && describedKey !== null && describedKey === describeItemsKey(cart.items)

  const itemCount = cart.items.reduce((sum, item) => sum + item.quantity, 0)

  const reconcileFromServer = useCallback(async (sessionId: string): Promise<{ removedCount: number }> => {
    if (!sessionId) return { removedCount: 0 }
    try {
      const res = await fetch(`/api/cart?sessionId=${encodeURIComponent(sessionId)}`)
      if (!res.ok) return { removedCount: 0 }
      const j = await res.json()
      const serverItems = (j?.data?.items || []) as Array<{
        id: string
        seatId: string | null
        ticketTypeId: string | null
        priceAtTime: number
        stale?: boolean
        staleReason?: string | null
        ticketType?: { id: string; name: string; price: number; isAddon?: boolean }
      }>
      if (!Array.isArray(serverItems)) return { removedCount: 0 }
      // Build a server lookup keyed by seatId (one server item per seat)
      const byseat = new Map<string, typeof serverItems[0]>()
      for (const si of serverItems) if (si.seatId) byseat.set(si.seatId, si)

      let removedCount = 0
      setCart(prev => {
        let changed = false
        const items: CartItem[] = []
        for (const it of prev.items) {
          // Only seated items are represented in the server cart at reconcile
          // time — a seated item the server flagged stale (hold released, seat
          // taken, ticket hidden), or that vanished from the server cart
          // entirely, is a ghost: drop it so it can't reach "Complete Order".
          // Non-seated items (GA/addons that predate server-cart creation)
          // are left untouched here to avoid pruning legitimately-unsynced
          // lines. Never prune season passes.
          if (it.seatId && !it.seasonId) {
            const si = byseat.get(it.seatId)
            if (!si || si.stale) { removedCount++; changed = true; continue }
            const ttName = si.ticketType?.name ?? it.ticketTypeName
            const ttId = si.ticketType?.id ?? it.ticketTypeId
            const price = Number(si.priceAtTime ?? it.price)
            if (it.ticketTypeId === ttId && it.ticketTypeName === ttName && it.price === price) {
              items.push(it)
            } else {
              changed = true
              items.push({ ...it, ticketTypeId: ttId, ticketTypeName: ttName, price, allInPrice: price })
            }
          } else {
            items.push(it)
          }
        }
        if (!changed) return prev
        return { ...prev, items, totals: recalculateLocalTotals(items) }
      })
      return { removedCount }
    } catch {
      // Silent — UI keeps showing whatever it already had if server is unreachable.
      return { removedCount: 0 }
    }
  }, [])

  return (
    <CartContext.Provider
      value={{
        cart,
        isOpen,
        itemCount,
        addItem,
        removeItem,
        updateQuantity,
        applyPromoCode,
        removePromoCode,
        updateTotals,
        clearCart,
        toggleCart,
        openCart,
        closeCart,
        serverCartId,
        orderDescription,
        describingOrder,
        totalsVerified,
        describeFailed,
        retryDescribe,
        promoData,
        setPromoData,
        timeRemaining,
        createServerCart,
        syncCartToServer,
        describeCurrentOrder,
        syncExpiryFromServer,
        lockCart: () => { cartLockedRef.current = true },
        unlockCart: () => { cartLockedRef.current = false },
        reconcileFromServer,
      }}
    >
      {children}
    </CartContext.Provider>
  )
}
